'use client';

type PaymentMethodType = 'CARD' | 'QR' | 'CRYPTO' | 'EZCASH';

interface MethodCardsProps {
  selected: string | null;
  onSelect: (method: PaymentMethodType) => void;
  processingChargePercent?: number;
  bcoinBalance?: number | null;
}

export default function MethodCards({
  selected,
  onSelect,
  bcoinBalance,
}: MethodCardsProps) {
  const SHOW_BCOINS = true;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        {/* Card */}
        <button
          onClick={() => onSelect('CARD')}
          className={`relative flex flex-col items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all overflow-hidden min-h-[100px] ${
            selected === 'CARD' ? 'border-primary bg-primary/5 shadow-md shadow-primary/10' : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
        >
          {selected === 'CARD' && <CheckBadge className="bg-primary" />}
          <div className="relative z-10 py-1 flex items-center justify-center w-full">
            <div className="flex gap-1 h-5 md:h-6 items-center justify-center -mb-1">
              <img src="/assets/visa.png" alt="Visa" className="h-full object-contain drop-shadow-sm grayscale-[0.2]" />
              <svg viewBox="0 0 36 22" className="h-full w-auto grayscale-[0.2]">
                <circle cx="11.5" cy="11.5" r="11.5" fill="#EB001B"/>
                <circle cx="24.5" cy="11.5" r="11.5" fill="#F79E1B"/>
                <path fill="#FF5F00" d="M18 20.375A11.462 11.462 0 0113.625 11.5 11.462 11.462 0 018 2.625 11.462 11.462 0 0122.375 11.5 11.462 11.462 0 018 20.375z"/>
              </svg>
              <div className="bg-sky-600 px-1 py-0.5 rounded flex items-center justify-center font-black text-white text-[8px] tracking-tighter h-[80%] my-auto ml-0.5">
                AMEX
              </div>
            </div>
          </div>
          <div className="text-[9px] md:text-[10px] font-bold text-slate-800 uppercase tracking-wider text-center leading-tight">
            Card Payment
          </div>
        </button>

        {/* QR */}
        <button
          onClick={() => onSelect('QR')}
          className={`relative flex flex-col items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all overflow-hidden min-h-[100px] ${
            selected === 'QR' ? 'border-primary bg-primary/5 shadow-md shadow-primary/10' : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
        >
          {selected === 'QR' && <CheckBadge className="bg-primary" />}
          <div className="relative z-10 py-1 flex items-center justify-center w-full">
            <div className="flex items-center justify-center h-8 md:h-10 -mb-1">
              <img src="/assets/lankaqr.jpg" alt="LankaQR" className="h-full object-contain mix-blend-multiply" />
            </div>
          </div>
          <div className="text-[9px] md:text-[10px] font-bold text-slate-800 uppercase tracking-wider text-center leading-tight">
            Lanka QR
          </div>
        </button>

        {/* Crypto */}
        <button
          onClick={() => onSelect('CRYPTO')}
          className={`relative flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all overflow-hidden w-full min-h-[100px] ${
            selected === 'CRYPTO' ? 'border-emerald-500 bg-emerald-50/30 shadow-md shadow-emerald-100/50' : 'border-slate-200 bg-white hover:border-emerald-300'
          }`}
        >
          {selected === 'CRYPTO' && <CheckBadge className="bg-emerald-500" />}
          <div className="relative z-10 flex flex-col items-center gap-2 w-full">
            <div className="flex items-center justify-center w-full">
              <img src="/assets/crypto.svg" alt="Crypto" className="h-8 md:h-10 w-auto object-contain" />
            </div>
            <span className="text-[9px] md:text-[10px] font-bold text-slate-800 uppercase tracking-wider leading-tight text-center">
              Crypto Payment
            </span>
          </div>
        </button>

        {/* EZCASH */}
        {SHOW_BCOINS && (
          <button
            onClick={() => onSelect('EZCASH')}
            className={`relative flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all overflow-hidden w-full min-h-[100px] ${
              selected === 'EZCASH' ? 'border-primary bg-primary/5 shadow-md shadow-primary/10' : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            {selected === 'EZCASH' && <CheckBadge className="bg-primary" />}
            <div className="relative z-10 flex flex-col items-center gap-2 w-full">
              <div className="flex items-center justify-center w-full">
                <img src="/assets/ez-cash-logo.png" alt="eZ Cash" className="h-8 md:h-10 w-auto object-contain" />
              </div>
              <span className="text-[9px] md:text-[10px] font-bold uppercase tracking-wider leading-tight text-center">
                <span className="text-slate-800">eZ Cash</span>
                {bcoinBalance !== null && bcoinBalance !== undefined && (
                  <span className="text-emerald-600 font-bold"> ({bcoinBalance.toLocaleString()})</span>
                )}
              </span>
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

function CheckBadge({ className }: { className: string }) {
  return (
    <div className={`absolute top-0 right-0 w-8 h-8 z-0 ${className}`} style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}>
      <svg className="absolute top-1 right-1 w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/>
      </svg>
    </div>
  );
}
